/**
 * FormBuilder - Jotform-style Drag-and-Drop Form Editor
 * 
 * Features:
 * - Left panel: Field palette with draggable components
 * - Center panel: Canvas for building forms with sortable fields
 * - Right panel: Field configuration when a field is selected
 * - Bottom panel: Live preview
 */

import React, { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileText,
  Mail,
  Phone,
  MessageSquare,
  List,
  CheckCircle,
  CalendarDays,
  Hash,
  EyeOff,
  Link2,
  User,
  Building2,
  MapPin,
  Globe,
  Briefcase,
  GripVertical,
  Trash2,
  Copy,
  Settings,
  Eye,
  Palette,
  Zap,
  Plus,
  X,
  ChevronRight,
  Type,
  AlignLeft,
  Star,
  Image
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';

// Field type definitions with icons and categories
const FIELD_PALETTE = {
  basic: {
    label: 'Campos Básicos',
    fields: [
      { type: 'text', label: 'Texto Corto', icon: Type, defaultLabel: 'Texto' },
      { type: 'textarea', label: 'Texto Largo', icon: AlignLeft, defaultLabel: 'Comentarios' },
      { type: 'email', label: 'Email', icon: Mail, defaultLabel: 'Correo Electrónico' },
      { type: 'phone', label: 'Teléfono', icon: Phone, defaultLabel: 'Teléfono' },
      { type: 'number', label: 'Número', icon: Hash, defaultLabel: 'Cantidad' },
      { type: 'date', label: 'Fecha', icon: CalendarDays, defaultLabel: 'Fecha' },
    ]
  },
  selection: {
    label: 'Selección',
    fields: [
      { type: 'select', label: 'Lista Desplegable', icon: List, defaultLabel: 'Seleccionar opción' },
      { type: 'checkbox', label: 'Casilla de Verificación', icon: CheckCircle, defaultLabel: 'Acepto los términos' },
      { type: 'radio', label: 'Opción Múltiple', icon: Star, defaultLabel: 'Seleccione una opción' },
    ]
  },
  contact: {
    label: 'Información de Contacto',
    fields: [
      { type: 'fullname', label: 'Nombre Completo', icon: User, defaultLabel: 'Nombre Completo' },
      { type: 'company', label: 'Empresa', icon: Building2, defaultLabel: 'Empresa' },
      { type: 'address', label: 'Dirección', icon: MapPin, defaultLabel: 'Dirección' },
      { type: 'website', label: 'Sitio Web', icon: Globe, defaultLabel: 'Sitio Web' },
      { type: 'position', label: 'Cargo', icon: Briefcase, defaultLabel: 'Cargo/Posición' },
    ]
  },
  advanced: {
    label: 'Avanzado',
    fields: [
      { type: 'hidden', label: 'Campo Oculto', icon: EyeOff, defaultLabel: 'campo_oculto' },
      { type: 'heading', label: 'Encabezado', icon: FileText, defaultLabel: 'Sección' },
      { type: 'paragraph', label: 'Texto Descriptivo', icon: MessageSquare, defaultLabel: '' },
      { type: 'divider', label: 'Separador', icon: ChevronRight, defaultLabel: '' },
    ]
  }
};

// CRM Mappings
const CRM_MAPPINGS = [
  { value: 'none', label: 'Sin mapeo', group: 'none' },
  // Empresa fields
  { value: 'empresa.name', label: 'Nombre', group: 'empresa' },
  { value: 'empresa.email', label: 'Email', group: 'empresa' },
  { value: 'empresa.phone', label: 'Teléfono', group: 'empresa' },
  { value: 'empresa.website', label: 'Sitio Web', group: 'empresa' },
  { value: 'empresa.industry', label: 'Industria', group: 'empresa' },
  { value: 'empresa.address', label: 'Dirección', group: 'empresa' },
  { value: 'empresa.city', label: 'Ciudad', group: 'empresa' },
  { value: 'empresa.state', label: 'Estado/Provincia', group: 'empresa' },
  { value: 'empresa.country', label: 'País', group: 'empresa' },
  { value: 'empresa.postal_code', label: 'Código Postal', group: 'empresa' },
  { value: 'empresa.description', label: 'Descripción', group: 'empresa' },
  { value: 'empresa.source', label: 'Fuente', group: 'empresa' },
  // Contacto fields
  { value: 'contacto.name', label: 'Nombre', group: 'contacto' },
  { value: 'contacto.email', label: 'Email', group: 'contacto' },
  { value: 'contacto.phone', label: 'Teléfono', group: 'contacto' },
  { value: 'contacto.position', label: 'Posición/Cargo', group: 'contacto' },
  { value: 'contacto.department', label: 'Departamento', group: 'contacto' },
  // Other
  { value: 'nota', label: 'Agregar a Notas', group: 'other' },
  { value: 'deal.service', label: 'Servicio', group: 'deal' },
  { value: 'deal.budget', label: 'Presupuesto', group: 'deal' },
];

// Sortable field in canvas
const SortableCanvasField = ({ field, isSelected, onSelect, onDelete, onDuplicate }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getFieldIcon = (type) => {
    const allFields = Object.values(FIELD_PALETTE).flatMap(cat => cat.fields);
    const found = allFields.find(f => f.type === type);
    return found?.icon || FileText;
  };

  const Icon = getFieldIcon(field.field_type);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        group relative rounded-lg border-2 transition-all duration-150
        ${isDragging ? 'opacity-50 z-50' : ''}
        ${isSelected 
          ? 'border-[#D4A843] bg-amber-50 shadow-md' 
          : 'border-gray-200 bg-white hover:border-gray-300'
        }
      `}
      onClick={() => onSelect(field.id)}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing hover:bg-gray-100 rounded-l-lg"
      >
        <GripVertical size={16} className="text-gray-400" />
      </div>

      {/* Field content */}
      <div className="pl-10 pr-24 py-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon size={14} className="text-gray-500" />
          <span className="text-xs text-gray-400 uppercase">{field.field_type}</span>
          {field.required && (
            <span className="text-xs text-red-500 font-medium">*Requerido</span>
          )}
        </div>
        <div className="font-medium text-gray-800">{field.label || 'Sin etiqueta'}</div>
        {field.placeholder && (
          <div className="text-sm text-gray-400 mt-1">{field.placeholder}</div>
        )}
        {field.crm_mapping && field.crm_mapping !== 'none' && (
          <div className="text-xs text-[#D4A843] mt-1 flex items-center gap-1">
            <Link2 size={10} />
            {CRM_MAPPINGS.find(m => m.value === field.crm_mapping)?.label || field.crm_mapping}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onDuplicate(field.id); }}
          className="p-1.5 rounded hover:bg-gray-100"
          title="Duplicar"
        >
          <Copy size={14} className="text-gray-500" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(field.id); }}
          className="p-1.5 rounded hover:bg-red-100"
          title="Eliminar"
        >
          <Trash2 size={14} className="text-red-500" />
        </button>
      </div>
    </div>
  );
};

// Canvas drop zone
const CanvasDropZone = ({ children, isEmpty }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'canvas-dropzone'
  });

  return (
    <div
      ref={setNodeRef}
      className={`
        min-h-[400px] rounded-xl border-2 border-dashed transition-colors p-4
        ${isOver ? 'border-[#D4A843] bg-amber-50' : 'border-gray-200'}
        ${isEmpty ? 'flex items-center justify-center' : ''}
      `}
    >
      {isEmpty ? (
        <div className="text-center text-gray-400">
          <Plus size={48} className="mx-auto mb-3 opacity-50" />
          <p className="text-lg">Arrastra campos aquí</p>
          <p className="text-sm mt-1">o haz clic en un campo del panel izquierdo</p>
        </div>
      ) : (
        children
      )}
    </div>
  );
};

// Field configuration panel
const FieldConfigPanel = ({ field, onChange, onClose }) => {
  if (!field) {
    return (
      <div className="h-full flex items-center justify-center text-center p-6">
        <div className="text-gray-400">
          <Settings size={48} className="mx-auto mb-3 opacity-50" />
          <p>Selecciona un campo para configurarlo</p>
        </div>
      </div>
    );
  }

  const updateField = (key, value) => {
    onChange({ ...field, [key]: value });
  };

  const isLayoutField = ['heading', 'paragraph', 'divider'].includes(field.field_type);

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between pb-3 border-b">
          <h3 className="font-semibold text-gray-800">Configurar Campo</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={16} />
          </button>
        </div>

        {/* Label */}
        {field.field_type !== 'divider' && (
          <div>
            <Label>Etiqueta</Label>
            <Input
              value={field.label || ''}
              onChange={(e) => updateField('label', e.target.value)}
              placeholder="Nombre del campo"
            />
          </div>
        )}

        {/* Placeholder - not for layout fields */}
        {!isLayoutField && (
          <div>
            <Label>Placeholder</Label>
            <Input
              value={field.placeholder || ''}
              onChange={(e) => updateField('placeholder', e.target.value)}
              placeholder="Texto de ayuda"
            />
          </div>
        )}

        {/* Help text */}
        {!isLayoutField && (
          <div>
            <Label>Texto de Ayuda</Label>
            <Input
              value={field.help_text || ''}
              onChange={(e) => updateField('help_text', e.target.value)}
              placeholder="Instrucciones para el usuario"
            />
          </div>
        )}

        {/* Required toggle - not for layout fields */}
        {!isLayoutField && (
          <div className="flex items-center justify-between py-2">
            <div>
              <Label>Campo Requerido</Label>
              <p className="text-xs text-gray-500">El usuario debe completar este campo</p>
            </div>
            <Switch
              checked={field.required || false}
              onCheckedChange={(val) => updateField('required', val)}
            />
          </div>
        )}

        {/* Options for select/radio */}
        {['select', 'radio'].includes(field.field_type) && (
          <div>
            <Label>Opciones (una por línea)</Label>
            <Textarea
              value={(field.options || []).join('\n')}
              onChange={(e) => updateField('options', e.target.value.split('\n').filter(o => o.trim()))}
              placeholder="Opción 1&#10;Opción 2&#10;Opción 3"
              rows={4}
            />
          </div>
        )}

        {/* Default value for hidden fields */}
        {field.field_type === 'hidden' && (
          <div>
            <Label>Valor por Defecto</Label>
            <Input
              value={field.default_value || ''}
              onChange={(e) => updateField('default_value', e.target.value)}
              placeholder="Valor que se enviará"
            />
          </div>
        )}

        {/* Heading size */}
        {field.field_type === 'heading' && (
          <div>
            <Label>Tamaño</Label>
            <Select
              value={field.heading_size || 'h2'}
              onValueChange={(val) => updateField('heading_size', val)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="h1">Grande (H1)</SelectItem>
                <SelectItem value="h2">Mediano (H2)</SelectItem>
                <SelectItem value="h3">Pequeño (H3)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Paragraph content */}
        {field.field_type === 'paragraph' && (
          <div>
            <Label>Contenido</Label>
            <Textarea
              value={field.content || ''}
              onChange={(e) => updateField('content', e.target.value)}
              placeholder="Texto descriptivo..."
              rows={4}
            />
          </div>
        )}

        {/* CRM Mapping - not for layout fields */}
        {!isLayoutField && (
          <div className="pt-3 border-t">
            <Label>Mapeo al CRM</Label>
            <p className="text-xs text-gray-500 mb-2">Guardar este campo en el CRM</p>
            <Select
              value={field.crm_mapping || 'none'}
              onValueChange={(val) => updateField('crm_mapping', val === 'none' ? '' : val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar mapeo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin mapeo</SelectItem>
                <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50">Empresa</div>
                {CRM_MAPPINGS.filter(m => m.group === 'empresa').map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
                <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50">Contacto</div>
                {CRM_MAPPINGS.filter(m => m.group === 'contacto').map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
                <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50">Otro</div>
                {CRM_MAPPINGS.filter(m => m.group === 'other' || m.group === 'deal').map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

// Form preview
const FormPreview = ({ formData, fields }) => {
  const renderField = (field) => {
    const baseInputClass = "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent";
    
    switch (field.field_type) {
      case 'heading':
        const HeadingTag = field.heading_size || 'h2';
        const sizeClasses = {
          h1: 'text-2xl',
          h2: 'text-xl',
          h3: 'text-lg'
        };
        return (
          <HeadingTag className={`${sizeClasses[HeadingTag]} font-bold text-gray-800`}>
            {field.label}
          </HeadingTag>
        );
      
      case 'paragraph':
        return <p className="text-gray-600">{field.content || field.label}</p>;
      
      case 'divider':
        return <hr className="border-gray-200 my-4" />;
      
      case 'textarea':
        return (
          <textarea
            className={baseInputClass}
            placeholder={field.placeholder}
            rows={3}
            disabled
          />
        );
      
      case 'select':
        return (
          <select className={baseInputClass} disabled>
            <option>{field.placeholder || 'Seleccionar...'}</option>
            {(field.options || []).map((opt, i) => (
              <option key={i}>{opt}</option>
            ))}
          </select>
        );
      
      case 'checkbox':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" disabled className="w-4 h-4" />
            <span className="text-gray-700">{field.placeholder || field.label}</span>
          </label>
        );
      
      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options || ['Opción 1', 'Opción 2']).map((opt, i) => (
              <label key={i} className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name={field.id} disabled className="w-4 h-4" />
                <span className="text-gray-700">{opt}</span>
              </label>
            ))}
          </div>
        );
      
      case 'hidden':
        return (
          <div className="px-3 py-2 bg-gray-100 rounded-md text-sm text-gray-500 italic">
            Campo oculto: {field.default_value || '(sin valor)'}
          </div>
        );
      
      default:
        return (
          <input
            type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
            className={baseInputClass}
            placeholder={field.placeholder}
            disabled
          />
        );
    }
  };

  return (
    <div
      className="rounded-xl overflow-hidden shadow-lg"
      style={{ 
        backgroundColor: formData.background_color || '#ffffff',
        '--primary-color': formData.primary_color || '#D4A843'
      }}
    >
      {/* Header */}
      <div 
        className="p-6 text-white"
        style={{ backgroundColor: formData.primary_color || '#D4A843' }}
      >
        {formData.logo_url && (
          <img src={formData.logo_url} alt="Logo" className="h-10 mb-4" />
        )}
        <h2 className="text-xl font-bold">{formData.name || 'Nombre del Formulario'}</h2>
        {formData.description && (
          <p className="text-sm mt-1 opacity-90">{formData.description}</p>
        )}
      </div>

      {/* Fields */}
      <div className="p-6 space-y-4">
        {fields.length === 0 ? (
          <p className="text-center text-gray-400 py-8">
            Agrega campos para ver la vista previa
          </p>
        ) : (
          fields.map(field => (
            <div key={field.id}>
              {!['heading', 'paragraph', 'divider'].includes(field.field_type) && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
              )}
              {renderField(field)}
              {field.help_text && (
                <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
              )}
            </div>
          ))
        )}
        
        {/* Submit button */}
        {fields.length > 0 && (
          <button
            className="w-full py-3 rounded-md text-white font-medium mt-6"
            style={{ backgroundColor: formData.primary_color || '#D4A843' }}
            disabled
          >
            {formData.submit_button_text || 'Enviar'}
          </button>
        )}
      </div>
    </div>
  );
};

// Main FormBuilder component
const FormBuilder = ({ 
  formData, 
  setFormData, 
  onSave, 
  saving,
  isEdit = false 
}) => {
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [activeTab, setActiveTab] = useState('fields');
  const [draggedField, setDraggedField] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fields = formData.fields || [];

  const selectedField = selectedFieldId 
    ? fields.find(f => f.id === selectedFieldId) 
    : null;

  // Generate unique field ID
  const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Add field from palette
  const addField = useCallback((fieldDef) => {
    const newField = {
      id: generateFieldId(),
      field_type: fieldDef.type,
      label: fieldDef.defaultLabel,
      placeholder: '',
      required: false,
      options: fieldDef.type === 'select' || fieldDef.type === 'radio' ? ['Opción 1', 'Opción 2', 'Opción 3'] : [],
      crm_mapping: '',
      help_text: '',
      default_value: ''
    };
    
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
    
    setSelectedFieldId(newField.id);
  }, [setFormData]);

  // Update field
  const updateField = useCallback((updatedField) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === updatedField.id ? updatedField : f)
    }));
  }, [setFormData]);

  // Delete field
  const deleteField = useCallback((fieldId) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null);
    }
  }, [setFormData, selectedFieldId]);

  // Duplicate field
  const duplicateField = useCallback((fieldId) => {
    const field = fields.find(f => f.id === fieldId);
    if (!field) return;
    
    const idx = fields.findIndex(f => f.id === fieldId);
    const newField = {
      ...field,
      id: generateFieldId(),
      label: `${field.label} (copia)`
    };
    
    const newFields = [...fields];
    newFields.splice(idx + 1, 0, newField);
    
    setFormData(prev => ({
      ...prev,
      fields: newFields
    }));
    
    setSelectedFieldId(newField.id);
  }, [fields, setFormData]);

  // Handle drag events
  const handleDragStart = (event) => {
    const { active } = event;
    
    if (active.data.current?.type === 'palette-field') {
      setDraggedField(active.data.current.field);
    } else {
      const field = fields.find(f => f.id === active.id);
      setDraggedField(field);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setDraggedField(null);

    if (!over) return;

    // Adding from palette
    if (active.data.current?.type === 'palette-field') {
      addField(active.data.current.field);
      return;
    }

    // Reordering within canvas
    if (active.id !== over.id) {
      const oldIndex = fields.findIndex(f => f.id === active.id);
      const newIndex = fields.findIndex(f => f.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        setFormData(prev => ({
          ...prev,
          fields: arrayMove(prev.fields, oldIndex, newIndex)
        }));
      }
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex h-[calc(100vh-200px)] min-h-[600px] bg-gray-50 rounded-xl overflow-hidden border border-gray-200">
        {/* Left Panel - Field Palette */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-800">Componentes</h3>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-4">
              {Object.entries(FIELD_PALETTE).map(([key, category]) => (
                <div key={key}>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    {category.label}
                  </h4>
                  <div className="space-y-1.5">
                    {category.fields.map(field => (
                      <div
                        key={field.type}
                        onClick={() => addField(field)}
                        className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-white 
                          hover:border-[#D4A843] hover:bg-amber-50 cursor-pointer
                          transition-all duration-150"
                      >
                        <field.icon size={16} className="text-[#D4A843]" />
                        <span className="text-sm text-gray-700">{field.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Center Panel - Canvas & Preview */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="bg-white border-b border-gray-200 px-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-transparent h-12">
                <TabsTrigger 
                  value="fields" 
                  className="data-[state=active]:bg-amber-100 data-[state=active]:text-[#D4A843]"
                >
                  <Settings size={16} className="mr-2" />
                  Diseñar
                </TabsTrigger>
                <TabsTrigger 
                  value="preview"
                  className="data-[state=active]:bg-amber-100 data-[state=active]:text-[#D4A843]"
                >
                  <Eye size={16} className="mr-2" />
                  Vista Previa
                </TabsTrigger>
                <TabsTrigger 
                  value="settings"
                  className="data-[state=active]:bg-amber-100 data-[state=active]:text-[#D4A843]"
                >
                  <Palette size={16} className="mr-2" />
                  Apariencia
                </TabsTrigger>
                <TabsTrigger 
                  value="automation"
                  className="data-[state=active]:bg-amber-100 data-[state=active]:text-[#D4A843]"
                >
                  <Zap size={16} className="mr-2" />
                  Automatización
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'fields' && (
              <div className="h-full flex">
                {/* Canvas */}
                <ScrollArea className="flex-1 p-6">
                  <div className="max-w-2xl mx-auto">
                    {/* Form name input */}
                    <div className="mb-6">
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Nombre del formulario"
                        className="text-xl font-semibold border-0 border-b rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#D4A843]"
                        data-testid="form-builder-name"
                      />
                      <Input
                        value={formData.description || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Descripción del formulario (opcional)"
                        className="text-sm text-gray-500 border-0 px-0 mt-1 focus-visible:ring-0"
                      />
                    </div>

                    {/* Fields Canvas */}
                    <SortableContext
                      items={fields.map(f => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <CanvasDropZone isEmpty={fields.length === 0}>
                        <div className="space-y-2">
                          {fields.map(field => (
                            <SortableCanvasField
                              key={field.id}
                              field={field}
                              isSelected={selectedFieldId === field.id}
                              onSelect={setSelectedFieldId}
                              onDelete={deleteField}
                              onDuplicate={duplicateField}
                            />
                          ))}
                        </div>
                      </CanvasDropZone>
                    </SortableContext>
                  </div>
                </ScrollArea>

                {/* Right Panel - Field Config */}
                <div className="w-72 bg-white border-l border-gray-200">
                  <FieldConfigPanel
                    field={selectedField}
                    onChange={updateField}
                    onClose={() => setSelectedFieldId(null)}
                  />
                </div>
              </div>
            )}

            {activeTab === 'preview' && (
              <ScrollArea className="h-full p-6">
                <div className="max-w-lg mx-auto">
                  <FormPreview formData={formData} fields={fields} />
                </div>
              </ScrollArea>
            )}

            {activeTab === 'settings' && (
              <ScrollArea className="h-full p-6">
                <div className="max-w-xl mx-auto space-y-6">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Palette size={20} className="text-[#D4A843]" />
                    Apariencia del Formulario
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Color Principal</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={formData.primary_color || '#D4A843'}
                          onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer border border-gray-200"
                        />
                        <Input
                          value={formData.primary_color || '#D4A843'}
                          onChange={(e) => setFormData(prev => ({ ...prev, primary_color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Color de Fondo</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="color"
                          value={formData.background_color || '#ffffff'}
                          onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                          className="w-10 h-10 rounded cursor-pointer border border-gray-200"
                        />
                        <Input
                          value={formData.background_color || '#ffffff'}
                          onChange={(e) => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label>URL del Logo (opcional)</Label>
                    <Input
                      value={formData.logo_url || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                      placeholder="https://..."
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Texto del Botón</Label>
                      <Input
                        value={formData.submit_button_text || 'Enviar'}
                        onChange={(e) => setFormData(prev => ({ ...prev, submit_button_text: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>URL de Redirección</Label>
                      <Input
                        value={formData.redirect_url || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, redirect_url: e.target.value }))}
                        placeholder="https://..."
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Mensaje de Éxito</Label>
                    <Textarea
                      value={formData.success_message || '¡Gracias! Tu información ha sido recibida.'}
                      onChange={(e) => setFormData(prev => ({ ...prev, success_message: e.target.value }))}
                      rows={2}
                      className="mt-1"
                    />
                  </div>

                  {/* Preview card */}
                  <div className="pt-4 border-t">
                    <Label className="mb-2 block">Vista Previa del Botón</Label>
                    <button
                      className="px-6 py-2 rounded-md text-white font-medium"
                      style={{ backgroundColor: formData.primary_color || '#D4A843' }}
                    >
                      {formData.submit_button_text || 'Enviar'}
                    </button>
                  </div>
                </div>
              </ScrollArea>
            )}

            {activeTab === 'automation' && (
              <ScrollArea className="h-full p-6">
                <div className="max-w-xl mx-auto space-y-6">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <Zap size={20} className="text-[#D4A843]" />
                    Automatización CRM
                  </h3>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <Label>Crear Empresa automáticamente</Label>
                        <p className="text-xs text-gray-500">Crea una empresa con los datos mapeados</p>
                      </div>
                      <Switch
                        checked={formData.auto_create_empresa ?? true}
                        onCheckedChange={(val) => setFormData(prev => ({ ...prev, auto_create_empresa: val }))}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <Label>Crear Contacto automáticamente</Label>
                        <p className="text-xs text-gray-500">Agrega el contacto a la empresa</p>
                      </div>
                      <Switch
                        checked={formData.auto_create_contacto ?? true}
                        onCheckedChange={(val) => setFormData(prev => ({ ...prev, auto_create_contacto: val }))}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <Label>Crear Tarea de Seguimiento</Label>
                        <p className="text-xs text-gray-500">Crea una tarea para dar seguimiento</p>
                      </div>
                      <Switch
                        checked={formData.auto_create_task ?? false}
                        onCheckedChange={(val) => setFormData(prev => ({ ...prev, auto_create_task: val }))}
                      />
                    </div>

                    {formData.auto_create_task && (
                      <div>
                        <Label>Título de la Tarea</Label>
                        <Input
                          value={formData.task_title_template || 'Seguimiento: {empresa_name}'}
                          onChange={(e) => setFormData(prev => ({ ...prev, task_title_template: e.target.value }))}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Variables: {'{empresa_name}'}, {'{contacto_name}'}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Webhook section */}
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div>
                        <Label className="text-orange-800">Webhook n8n</Label>
                        <p className="text-xs text-orange-600">Envía datos a n8n al enviar</p>
                      </div>
                      <Switch
                        checked={formData.webhook_enabled ?? false}
                        onCheckedChange={(val) => setFormData(prev => ({ ...prev, webhook_enabled: val }))}
                      />
                    </div>
                    
                    {formData.webhook_enabled && (
                      <div className="mt-3">
                        <Label>URL del Webhook</Label>
                        <Input
                          value={formData.webhook_url || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, webhook_url: e.target.value }))}
                          placeholder="https://n8n.tudominio.com/webhook/..."
                          className="mt-1"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>
            )}
          </div>
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {draggedField && (
          <div className="p-3 bg-white border-2 border-[#D4A843] rounded-lg shadow-lg opacity-90">
            <span className="font-medium text-gray-700">
              {draggedField.label || draggedField.defaultLabel}
            </span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default FormBuilder;
